import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { fmtMoney, MAX_PILLS } from "@/lib/pharmacy";
import { toast } from "sonner";
import { ShieldAlert, Lock, Wallet } from "lucide-react";

const initialForm = {
  full_name: "", email: "", phone: "", country: "", address_line: "",
  city: "", state: "", postal_code: "", notes: "",
};

const PAYMENT_METHODS = [
  { id: "PayPal", desc: "Convenient online payments where supported.", color: "border-blue-200 bg-blue-50/40 text-blue-700" },
  { id: "Western Union", desc: "Transfer services where permitted and available.", color: "border-amber-200 bg-amber-50/40 text-amber-700" },
  { id: "Razorpay", desc: "Secure online payment options for eligible transactions.", color: "border-emerald-200 bg-emerald-50/40 text-emerald-700" },
];

export default function Checkout() {
  const { items, totals, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    ...initialForm,
    full_name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
  });
  const [ack, setAck] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (items.length === 0) {
    return (
      <div className="blz-container py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Nothing to checkout</h1>
        <Link to="/catalogue" className="btn-primary mt-6">Browse catalogue</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (totals.error) return toast.error(totals.error);
    if (totals.requiresRx && !ack) return toast.error("Please acknowledge the prescription requirement.");
    if (!paymentMethod) return toast.error("Please choose a payment method.");
    for (const k of ["full_name","email","phone","country","address_line","city","state","postal_code"]) {
      if (!form[k].trim()) return toast.error("Please complete all required fields.");
    }
    setBusy(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.productId, pack_size: i.packSize, quantity: i.quantity })),
        shipping: form,
        prescription_ack: ack,
        payment_method: paymentMethod,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      nav(`/order/${data.order_number}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to place order");
    } finally { setBusy(false); }
  };

  return (
    <div className="blz-container py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Checkout</h1>
      <p className="text-sm text-slate-500 mt-1">Payment is handled offline after order review. Your card is never charged automatically.</p>

      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-flat p-6">
            <h2 className="font-display font-bold text-lg mb-4">Delivery details</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ["full_name","Full name"],["email","Email"],["phone","Phone"],["country","Country"],
                ["address_line","Address line"],["city","City"],["state","State / Province"],["postal_code","Postal / ZIP code"],
              ].map(([k,label]) => (
                <label key={k} className="block">
                  <span className="text-xs text-slate-600 font-semibold">{label}</span>
                  <input value={form[k]} onChange={onChange(k)} required className="mt-1 w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" data-testid={`checkout-${k}`}/>
                </label>
              ))}
              <label className="block sm:col-span-2">
                <span className="text-xs text-slate-600 font-semibold">Additional notes (optional)</span>
                <textarea value={form.notes} onChange={onChange("notes")} rows={3} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="checkout-notes"/>
              </label>
            </div>
          </div>

          <div className="card-flat p-6" data-testid="payment-picker">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-5 w-5 text-emerald-600"/>
              <h2 className="font-display font-bold text-lg">How would you like to pay?</h2>
            </div>
            <p className="text-sm text-slate-500 mb-4">Choose one — our team will share the exact instructions for your method after you place the order.</p>
            <div className="grid sm:grid-cols-3 gap-3">
              {PAYMENT_METHODS.map((m) => (
                <label
                  key={m.id}
                  className={`relative cursor-pointer rounded-xl border-2 p-4 transition ${paymentMethod === m.id ? `${m.color} ring-2 ring-emerald-600/40` : "border-slate-200 hover:border-slate-400"}`}
                  data-testid={`payment-${m.id.toLowerCase().replace(/\s/g,"-")}`}
                >
                  <input
                    type="radio"
                    name="payment_method"
                    value={m.id}
                    checked={paymentMethod === m.id}
                    onChange={() => setPaymentMethod(m.id)}
                    className="sr-only"
                  />
                  <div className="font-display font-bold text-slate-900">{m.id}</div>
                  <div className="text-xs text-slate-600 mt-1">{m.desc}</div>
                  {paymentMethod === m.id && (
                    <div className="absolute top-2 right-2 text-emerald-600 text-xs font-bold">✓</div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {totals.requiresRx && (
            <div className="card-flat p-6 border-blue-200 bg-blue-50/40">
              <div className="flex items-center gap-2 text-blue-700 font-semibold"><ShieldAlert className="h-4 w-4"/> Prescription verification required</div>
              <p className="text-sm text-slate-700 mt-2">One or more items in your cart require a valid prescription. Our licensed pharmacist will contact you at the email provided to review your prescription document before dispatch.</p>
              <label className="flex items-start gap-2 mt-3 text-sm">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="checkout-rx-ack" className="mt-1"/>
                <span>I confirm that I hold a valid prescription for the Rx-flagged items and I authorize Noyaa Pharma to verify it before dispatch.</span>
              </label>
            </div>
          )}
        </div>

        <aside className="card-flat p-6 h-fit sticky top-20">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Order summary</div>
          <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
            {items.map((i) => (
              <li key={`${i.productId}-${i.packSize}`} className="py-2 flex gap-3 text-sm">
                <img src={i.image} className="w-10 h-10 rounded object-cover bg-slate-100" alt=""/>
                <div className="flex-1"><div className="font-medium truncate">{i.name}</div><div className="text-xs text-slate-500 font-mono">{i.packSize}p × {i.quantity}</div></div>
                <div className="font-mono text-sm">{fmtMoney(i.unitPrice * i.quantity)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <div className="text-xs text-slate-500 flex justify-between mb-1"><span>Pill total</span><span className="font-mono">{totals.totalPills} / {MAX_PILLS}</span></div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full ${totals.error ? "bg-red-500" : "bg-emerald-600"}`} style={{ width: `${Math.min(100, (totals.totalPills / MAX_PILLS) * 100)}%` }}/></div>
          </div>
          {totals.error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-2" data-testid="checkout-error">{totals.error}</div>}
          <div className="space-y-1 text-sm mt-4">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono">{fmtMoney(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Discount</span><span className="font-mono">-{fmtMoney(0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-600">Shipping</span><span className="font-mono">{fmtMoney(totals.shipping)}</span></div>
            <div className="flex justify-between font-bold text-slate-900 pt-2 border-t border-slate-100"><span>Grand total</span><span className="font-mono" data-testid="checkout-grand">{fmtMoney(totals.grand)}</span></div>
            {paymentMethod && (
              <div className="flex justify-between text-xs text-slate-500 pt-2"><span>Payment via</span><span className="font-semibold text-slate-900">{paymentMethod}</span></div>
            )}
          </div>
          <button type="submit" disabled={busy || !!totals.error} className="btn-primary w-full mt-4" data-testid="checkout-place-order">
            <Lock className="h-4 w-4"/> {busy ? "Placing order..." : "Place order"}
          </button>
          <div className="text-[11px] text-slate-400 text-center mt-2">By placing an order you accept our Terms & Privacy Policy.</div>
        </aside>
      </form>
    </div>
  );
}
